import type { JSX } from 'react'
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { wsRoom } from '@apiary/shared'
import type { McAccountDto } from '@/lib/dto'
import { api } from '@/lib/api'
import { qk } from '@/lib/query-keys'
import { useMcAccounts } from '@/hooks/queries'
import { useWsRoom } from '@/hooks/useWsRoom'
import { useCanOperate, useSessionStore } from '@/stores/session'
import { DEFAULT_TOAST_MS, UNDO_TOAST_MS, useToastStore } from '@/stores/toasts'
import { ConfirmDestructive } from '@/components/ConfirmDestructive'
import { Button } from '@/ui/primitives/Button'
import { SectionHead } from '@/ui/primitives/Surface'
import { EmptyBlock, ErrorBlock, SkeletonGrid } from '../shared/states'
import { problemText } from '../shared/problem'
import { AccountDialog } from './AccountDialog'
import { AccountTile } from './AccountTile'
import { DeviceCodeDialog } from './DeviceCodeDialog'

/**
 * Экран Minecraft-аккаунтов: чем бот входит на сервер.
 *
 * Аккаунт — единственная сущность панели, у которой есть внешний срок годности:
 * токен Microsoft истекает, и бот падает в AUTH_FAILED посреди ночи. Поэтому
 * экран построен вокруг двух вопросов — «жив ли токен» и «кем аккаунт занят», а
 * не вокруг CRUD.
 */

const GRID = 'grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3'

export function AccountsScreen(): JSX.Element {
  const queryClient = useQueryClient()
  const push = useToastStore(state => state.push)
  const orgId = useSessionStore(state => state.activeOrgId)
  useWsRoom(orgId === null ? null : wsRoom.org(orgId))

  const accounts = useMcAccounts()
  const canOperate = useCanOperate()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<McAccountDto | null>(null)
  const [deviceAccountId, setDeviceAccountId] = useState<string | null>(null)
  const [toDelete, setToDelete] = useState<McAccountDto | null>(null)

  const reauth = useMutation({
    mutationFn: (id: string) => api.mcAccounts.reauth(id),
    onSuccess: (_data, id) => setDeviceAccountId(id),
    onError: error =>
      push({ message: problemText(error), tone: 'danger', timeoutMs: DEFAULT_TOAST_MS })
  })

  const remove = useMutation({
    // Принимаем аккаунт целиком, а не id: в тосте нужно название, а к моменту
    // ответа сервера список уже перечитан и удалённой записи в нём нет.
    mutationFn: (account: McAccountDto) => api.mcAccounts.remove(account.id),
    onSuccess: (_data, account) => {
      void queryClient.invalidateQueries({ queryKey: qk.mcAccounts.list })
      push({
        message: `Аккаунт «${account.label}» удалён`,
        tone: 'warning',
        timeoutMs: UNDO_TOAST_MS
      })
    },
    onError: error =>
      push({ message: problemText(error), tone: 'danger', timeoutMs: DEFAULT_TOAST_MS })
  })

  const openCreate = (): void => {
    setEditing(null)
    setDialogOpen(true)
  }

  const openEdit = (account: McAccountDto): void => {
    setEditing(account)
    setDialogOpen(true)
  }

  const items = accounts.data?.items ?? []

  return (
    <>
      <SectionHead
        actions={
          <Button
            variant="primary"
            disabled={!canOperate}
            title={canOperate ? undefined : 'Недостаточно прав'}
            onClick={openCreate}
          >
            <Plus aria-hidden className="size-4" />
            Добавить аккаунт
          </Button>
        }
      />

      {accounts.isPending ? (
        <SkeletonGrid count={3} className={GRID} tileClassName="h-64" />
      ) : accounts.isError ? (
        <ErrorBlock error={accounts.error} onRetry={() => void accounts.refetch()} />
      ) : items.length === 0 ? (
        <EmptyBlock
          title="Аккаунтов пока нет"
          note="Аккаунт — это то, чем бот входит на сервер. Для лицензии выберите Microsoft: панель попросит код входа и не будет знать пароль. Для своего сервера без проверки лицензии хватит offline-ника."
          action={{
            label: 'Добавить аккаунт',
            onClick: openCreate,
            disabled: !canOperate,
            ...(canOperate ? {} : { title: 'Недостаточно прав' })
          }}
        />
      ) : (
        <div className={GRID}>
          {items.map((account, index) => (
            <div
              key={account.id}
              className="anim-rise"
              style={{ '--i': Math.min(index, 12) } as never}
            >
              <AccountTile
                account={account}
                canOperate={canOperate}
                reauthing={reauth.isPending && reauth.variables === account.id}
                onReauth={() => reauth.mutate(account.id)}
                onEdit={() => openEdit(account)}
                onDelete={() => setToDelete(account)}
              />
            </div>
          ))}
        </div>
      )}

      <AccountDialog
        open={dialogOpen}
        account={editing}
        onClose={() => setDialogOpen(false)}
        onNeedsAuth={setDeviceAccountId}
      />

      <DeviceCodeDialog
        open={deviceAccountId !== null}
        accountId={deviceAccountId}
        onClose={() => setDeviceAccountId(null)}
        onRetry={() => {
          if (deviceAccountId !== null) reauth.mutate(deviceAccountId)
        }}
        retrying={reauth.isPending}
      />

      <ConfirmDestructive
        open={toDelete !== null}
        onClose={() => setToDelete(null)}
        onConfirm={() => {
          if (toDelete === null) return
          remove.mutate(toDelete)
          setToDelete(null)
        }}
        title={`Удалить аккаунт «${toDelete?.label ?? ''}»?`}
        body="Токены аккаунта будут стёрты. Боты, которые им пользовались, останутся — но войти не смогут, пока не выберете другой аккаунт."
        expected={toDelete?.label ?? ''}
        loading={remove.isPending}
      />
    </>
  )
}
